<template>
  <CommonNoticeCard
    symbol-icon
    :icon="issueTypeIcon"
    :title="issue.summary"
    :class="[b(), 'cursor-pointer']"
  >
    <div
      slot="content"
      :class="b('tips')"
    >
      <div v-if="showNoSprintTips">
        <div :class="b('desc')">
          {{ $t('notice.noSprintTips') }}
        </div>
      </div>
      <div v-if="issue.tip">
        {{ issue.tip }}
      </div>
    </div>
    <template #actions>
      <div v-if="showNoSprintTips" class="zk-issue-notice-card_footer-left" >
        <el-link
          v-if="canMoveToCurrentSprint"
          style="font-size: 12px;font-weight: 500;"
          type="primary"
          :underline="false"
          :class="[b('move')]"
          size="mini"
          :disabled="moveSprintLoading.loading"
          @click.stop="moveSprintLoading.fn"
        >
          {{ $t('notice.moveToCurrentSprint') }}
        </el-link>
      </div>
      <el-tooltip :content="$t('button.positionView')">
        <el-button
          v-if="showPosition"
          type="plaintext2"
          size="mini"
          icon="zkicon iconicon_dingwei"
          @click.stop="position"
        />
      </el-tooltip>
      <el-button
        v-clipboard
        type="plaintext2"
        size="mini"
        icon="zkicon iconlianjie"
        :data-clipboard-text="linkUrl"
        @clipboard-success="onCopySuccess"
        @click.stop
      >
        {{ $t('button.copyLink') }}
      </el-button>
      <el-button
        type="plaintext2"
        size="mini"
        :icon="pinIcon"
        :disabled="pinLoading.loading"
        @click.stop="pinLoading.fn"
      >
        {{ hasPin? $t('Pin.CancelPin') : $t('Pin.pin') }}
      </el-button>
    </template>
  </CommonNoticeCard>
</template>

<script>
import CommonNoticeCard from '@/components/notice/CommonNoticeCard';
import { createLoadingEvent, findDownComponent, resolveUrl } from '@zk/framework-vue';
import theme from '@zk/theme/lib/variables.scss';
import { noop } from 'lodash';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'IssueNoticeCard',
  components: { CommonNoticeCard },
  bemNS: theme.bemNS,
  props: {
    issue: {
      type: Object,
      required: true
    },
    root: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      moveSprintLoading: createLoadingEvent(this.moveToCurrentSprint),
      pinLoading: createLoadingEvent(this.pin),
      kanbanPageInstance: null,
      moved: false,
      showPosition: false
    };
  },
  computed: {
    ...mapGetters('user', ['isProjectInnerCollaboratorButNotAdmin']),
    issueTypeIcon() {
      return this.issue.issueTypeIdVO?.iconUrl;
    },
    /**
     * 当前是否为跟进迭代页面
     */
    isCurrentKanbanPage() {
      return this.root.$route.name === 'kanban';
    },
    /**
     * 看板页面当前选择的迭代
     * @returns {*}
     */
    kanbanCurrentSprint() {
      return this.kanbanPageInstance?.sprint;
    },
    /**
     * 看板页面当前选择的项目
     */
    kanbanCurrentProject() {
      return this.kanbanPageInstance?.projectId;
    },
    /**
     * 是否可以移动到当前迭代
     */
    canMoveToCurrentSprint() {
      if (this.isProjectInnerCollaboratorButNotAdmin) {
        return false;
      }
      if (Array.isArray(this.kanbanCurrentSprint)) {
        return this.kanbanCurrentSprint.length === 1;
      }
      return !!this.kanbanCurrentSprint;
    },
    /**
     * issue的项目是否与看板是同一个项目
     */
    isSomeProject() {
      return this.issue.projectId === this.kanbanCurrentProject;
    },
    /**
     * 是否显示未关联迭代提示
     * @returns {boolean}
     */
    showNoSprintTips() {
      return this.isSomeProject && !this.issue.sprintVO && this.isCurrentKanbanPage && this.kanbanPageInstance?.isSprintFeatures && !this.moved;
    },
    linkUrl() {
      const url = resolveUrl(`${this.$config.VUE_APP_PMS_URL}/work/list?pid=${this.issue.projectId}&issueid=${this.issue.id}&suuid=${this.org.suuid}`);
      return `${url} ${this.issue.summary}`;
    },
    hasPin(){
      return this.$store.state.pin?.pinList?.findIndex(item => item?.issueVO?.issueId === this.issue.id) !== -1;
    },
    pinIcon(){
      return this.hasPin ? 'zkicon iconunPin' : 'zkicon iconPin';
    },
    ...mapState('user', ['org'])
  },
  watch: {
    isCurrentKanbanPage: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.kanbanPageInstance = null;
          return;
        }
        const kanbanApp = document.querySelector('#micro-app_kanban #app');
        if (kanbanApp) {
          this.kanbanPageInstance = findDownComponent(kanbanApp.__vue__, 'KanbanIndex');
        }
      }
    }
  },
  created() {
    const oldClick = this.$parent.onClick || noop;
    this.$parent.onClick = () => {
      oldClick();
      this.viewIssue();
    };
    this.checkShouldShowPosition();
  },
  destroyed() {
    this.$parent.onClick = null;
  },
  methods: {
    /**
     * 移动至当前迭代
     */
    async moveToCurrentSprint() {
      let sprint = this.kanbanCurrentSprint;
      if (!sprint){
        return;
      }
      if (Array.isArray(sprint)) {
        sprint = sprint[0];
      }
      await this.updateIssue({ sprint });
      this.moved = true;
      this.$parent.close();
    },
    /**
     * 查看事项详情
     */
    viewIssue() {
      this.eventBus.$emit('issue:edit', {
        id: this.issue.id
      });
    },
    onCopySuccess() {
      this.$message.success(this.$t('message.copySuccess'));
    },
    async pin() {
      if (this.hasPin){
        await this.$store.dispatch('pin/cancelPin', { id: this.issue.id });
        this.$message.success(this.$t('Pin.PinCanceled'));
      } else {
        await this.$store.dispatch('pin/addPin', { id: this.issue.id });
        this.$message.success(this.$t('Pin.success'));
      }
    },
    updateIssue(data) {
      return this.$http.post('/issue/update', { id: this.issue.id, data });
    },
    position() {
      this.eventBus.$emit('issue:notice-position', this.issue);
    },
    async checkShouldShowPosition(){
      const routeName = this.root.$route.name;
      if (routeName === 'epic-list'){
        if (!this.issue?.epic){
          return false;
        }
        const epicApp = document.querySelector('#micro-app_epic #app');
        if (!epicApp) {
          return false;
        }
        const epicPage = findDownComponent(epicApp.__vue__, 'EpicGantt');
        this.showPosition = await epicPage?.checkCreateIssueShouldShowPosition(this.issue);
        return;
      }
      if (routeName === 'gantt'){
        const projectApp = document.querySelector('#micro-app_project #app');
        if (!projectApp) {
          return false;
        }
        const ganttPage = findDownComponent(projectApp.__vue__, 'GanttView');
        this.showPosition = await ganttPage?.checkCreateIssueShouldShowPosition(this.issue);
        return;
      }
      if (routeName === 'roadmap'){
        const RoadmapComponentIndex = findDownComponent(this.root, 'RoadmapComponentIndex');
        this.showPosition = await RoadmapComponentIndex?.checkCreateIssueShouldShowPosition(this.issue);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@include b(issue-notice-card) {
  @include e(tips) {
    color: var(--font-color-2);
    line-height: 20px;
  }
  @include e(move){
    line-height: 20px;
    height: 20px;
    // padding: 0;
    ::v-deep{
      .el-button--content{
        line-height: 20px;
      }
    }
  }
  @include e(desc){
    margin-top:4px;
    font-size: 12px;
  }
  ::v-deep {
    .iconunPin{
      color:#ffc90c !important;
    }
  }
  .zk-issue-notice-card_footer-left{
    flex: 1;
    display: flex;
    align-items: center;
    // margin-right: 24px;
  }

}
</style>
